// The pool's order, as `PlayersQuery` promises it. PLAN-2026-09-24 W5.
//
// ONE COMPARATOR FOR EVERY HOST. It lived in the simulator's views until a real
// league's pool needed the same order; a second copy in the league store would
// be the order a demo shows and the order a real league shows, free to drift.
// Below both, beside the query it answers, the way `directoryFingerprint` sits
// beside `DirectoryQuery`.
//
// ORDERED BY A KEY, NOT BY A ROW, so a cursor can carry the key of the last row
// on a page and the next page can start after it even when that row is gone —
// a player somebody added since has left the free-agent list, and a page that
// looked for his row would have nothing to stand on.

import type { PlayerRowView, PlayerSort } from './views';

/** `trend` as a sort key: rising first. */
const TREND_RANK = { up: 2, flat: 1, down: 0 } as const;

/**
 * The figure a sort orders by; null when the row has none (and for `name`).
 * `stat` is the column key `sort=stat` orders by (B-412). A word for
 * `depth`, which reads A to Z. A row read with stats (`PlayersQuery.stats`)
 * orders by the range's points and points a game (B-518); one without, by
 * the season's.
 */
export function playerRowFigure(
  row: PlayerRowView,
  sort: PlayerSort,
  stat: string | null = null,
): number | string | null {
  switch (sort) {
    case 'projection':
      return row.projection;
    case 'points':
      return row.stats !== null ? row.stats.points : row.seasonPoints;
    case 'average':
      return row.stats !== null ? row.stats.average : row.seasonAvg;
    case 'recent':
      return row.lastThreeAvg;
    case 'trend':
      // The net adds where they were counted, the direction where only it was.
      return row.adds !== null ? row.adds : row.trend === null ? null : TREND_RANK[row.trend];
    case 'ownership':
      return row.ownershipPct;
    case 'name':
      return null;
    case 'form':
      return row.deeper?.form ?? null;
    case 'range':
      // The range's top: how high his best week went.
      return row.deeper?.range?.high ?? null;
    case 'low':
      // The range's bottom: how low his worst week went, the highest first.
      return row.deeper?.range?.low ?? null;
    case 'usage':
      return row.deeper?.usage?.share ?? null;
    case 'depth':
      // His place on his club's depth chart as its column and rank, "QB 01",
      // so the order groups the positions A to Z and runs each from its first
      // string down (B-479); the space keeps "RB" ahead of "RBX". Off the
      // chart is no figure.
      return row.player.depth
        ? `${row.player.depth.position} ${String(row.player.depth.rank).padStart(2, '0')}`
        : null;
    case 'stat':
      // One stat, or with none named the range's points. No games in the
      // range is no figure, after every row that has one.
      return stat === null ? (row.stats?.points ?? null) : (row.stats?.values[stat] ?? null);
  }
}

/** Where a row sits in the order: its figure under the sort, its name, its id. */
export type PlayerRowKey = readonly [figure: number | string | null, name: string, id: string];

export function playerRowKey(
  row: PlayerRowView,
  sort: PlayerSort,
  stat: string | null = null,
): PlayerRowKey {
  return [playerRowFigure(row, sort, stat), row.player.name, row.player.id];
}

/**
 * Highest first except by name, and A to Z by a word (`depth`'s), a null
 * figure after every other, and ties by name — then by id, so two players
 * with one name still have an order a cursor can resume from. `reverse`
 * (B-366) turns the figures round, or the names under `name`; a null figure
 * stays last, since a column sorted low to high that opened on every player
 * nobody has a number for would read as the list being empty, and ties stay
 * A to Z. A word compares by its characters, not the reader's locale, so
 * "RB 02" and "RBX 01" sit where their letters put them.
 */
export function comparePlayerKeys(sort: PlayerSort, reverse = false) {
  return (a: PlayerRowKey, b: PlayerRowKey): number => {
    const byName = a[1].localeCompare(b[1]) || (a[2] < b[2] ? -1 : a[2] > b[2] ? 1 : 0);
    if (sort === 'name') return reverse ? -byName : byName;
    const [x, y] = [a[0], b[0]];
    if (x === null || y === null) return x === y ? byName : x === null ? 1 : -1;
    if (typeof x === 'number' && typeof y === 'number') return (reverse ? x - y : y - x) || byName;
    const [p, q] = [String(x), String(y)];
    const az = p < q ? -1 : p > q ? 1 : 0;
    return (reverse ? -az : az) || byName;
  };
}

/** The same order over rows. */
export function comparePlayerRows(sort: PlayerSort, reverse = false, stat: string | null = null) {
  const byKey = comparePlayerKeys(sort, reverse);
  return (a: PlayerRowView, b: PlayerRowView): number =>
    byKey(playerRowKey(a, sort, stat), playerRowKey(b, sort, stat));
}

/**
 * Ranks within groups, as a table reads them: highest first, and TIES SHARE A
 * RANK — two at 14.2 are both 3rd, and the next is 5th. A value at or below
 * zero is not ranked: twenty backs at 0.0 are not 71st to 94th of anything.
 * One copy for the pool's ranks, the projection's and the season's, in the
 * store and the simulator alike.
 */
export function ranksWithin(
  entries: Iterable<{ id: string; group: string | undefined; value: number | null }>,
): Map<string, number> {
  const groups = new Map<string, { id: string; value: number }[]>();
  for (const e of entries) {
    if (e.group === undefined || e.value === null || e.value <= 0) continue;
    const list = groups.get(e.group) ?? [];
    list.push({ id: e.id, value: e.value });
    groups.set(e.group, list);
  }
  const ranks = new Map<string, number>();
  for (const list of groups.values()) {
    list.sort((x, y) => y.value - x.value);
    let rank = 0;
    list.forEach((e, n) => {
      if (n === 0 || e.value !== list[n - 1]!.value) rank = n + 1;
      ranks.set(e.id, rank);
    });
  }
  return ranks;
}
